import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Panel,
  ConnectionMode,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import DeleteOutlineOutlined from "@mui/icons-material/DeleteOutlineOutlined";
import FullscreenExitOutlined from "@mui/icons-material/FullscreenExitOutlined";
import SaveOutlined from "@mui/icons-material/SaveOutlined";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import ContentPasteOutlined from "@mui/icons-material/ContentPasteOutlined";
import FileCopyOutlined from "@mui/icons-material/FileCopyOutlined";
import { mergeEquipmentTypes } from "../equipmentTypes";
import { nodeTypes } from "../EquipmentNode";
import { edgeTypes } from "../EquipmentEdge";
import NodeInspector from "../NodeInspector";
import { useHostStatus } from "../hostStatusStore";
import NodeColorSwatches from "../NodeColorSwatches";
import { getAccessibleNodeColor } from "../nodeColorPalette";
import ModalTemplates from "../templates";
import ModalSalvarTemplate from "../templates/ModalSalvarTemplate";
import { getEdgeVisualStyle, resolveLineStyle } from "../templates/templateUtils";
import {
  ensureCustomEquipmentTypesLoaded,
  getCustomEquipmentTypesSnapshot,
  subscribeCustomEquipmentTypes,
} from "../customEquipmentTypes/customEquipmentTypesStore";
import { emptyNodeAnexosEntry } from "../hooks/useNodeAnexos";
import useDiagramaAtalhos from "../hooks/useDiagramaAtalhos";
import useAplicarTemplate from "../hooks/useAplicarTemplate";
import { getDiagramClipboard } from "../diagramClipboardStore";
import PaletaEquipamentos from "../PaletaEquipamentos";
import ControlesDiagrama from "../ControlesDiagrama";
import { getDiagramDotColor } from "../styles";
import styles from "./styles";
import InternoModalConfirmacaoSaida from "../../../../../../../components/modalSaindoDaPagina";

// Corpo interativo do canvas do diagrama com nós, arestas, paleta e painel de edição.
// Estado (diagrama/nodeAnexos) chega por props em vez de ref imperativo (Task 4 da
// hierarquia de circuitos) — quem instancia os hooks é circuito/index.js
export default function DiagramaCanvas({
  diagrama,
  nodeAnexos,
  diagramInstanceKey = "",
  editMode = false,
  isActive = true,
  isExpanded = false,
  onToggleExpanded = () => {},
  setSnackbar = () => {},
  circuitoPersisted = false,
  circuitoId = "",
  hostOptions = [],
  onSalvarCircuito,
  salvandoCircuito = false,
}) {
  // Tema atual da aplicação
  const theme = useTheme();

  // Estilos do canvas baseados no tema
  const canvasStyles = styles(theme);

  // Logo da barra de ações do canvas ampliado adaptada ao modo claro/escuro
  const logoExternoDark =
    window?.config?.REACT_APP_LOGO_EXTERNO_DARK ||
    process.env.REACT_APP_LOGO_EXTERNO_DARK ||
    "";
  const logoExternoLight =
    window?.config?.REACT_APP_LOGO_EXTERNO_LIGHT ||
    process.env.REACT_APP_LOGO_EXTERNO_LIGHT ||
    "";
  const logo =
    theme?.palette?.mode === "light"
      ? logoExternoLight || logoExternoDark
      : logoExternoDark || logoExternoLight;

  // Acessa ações de enquadramento e conversão de coordenadas do React Flow
  const { fitView, screenToFlowPosition } = useReactFlow();

  // Catálogo global de tipos de equipamento personalizados lido da store reativa
  const customEquipmentTypes = useSyncExternalStore(
    subscribeCustomEquipmentTypes,
    getCustomEquipmentTypesSnapshot,
  );

  // Confirmação de exclusão de equipamento que possui arquivos vinculados
  const [deleteNodeConfirm, setDeleteNodeConfirm] = useState(null);

  // Menu de contexto (botão direito) no canvas com coordenadas e tipo do elemento
  const [canvasContextMenu, setCanvasContextMenu] = useState(null);

  // Estado de nós, arestas, seleção e histórico do diagrama — instanciado em
  // circuito/index.js (useDiagramaState) e recebido aqui por props
  const {
    nodes,
    setNodes,
    edges,
    setEdges,
    handleNodesChange,
    onEdgesChange,
    onConnect,
    selectedNodeId,
    selectedEdgeId,
    setSelectedNodeId,
    setSelectedEdgeId,
    historyRef,
    redoRef,
    nodeEditSnapshotRef,
    edgeEditSnapshotRef,
    pushHistory,
    handleUndo,
    handleRedo,
    copySelection,
    selectAll,
  } = diagrama || {};

  // Anexos vinculados a nós do diagrama — instanciado em circuito/index.js
  // (useNodeAnexos) e recebido aqui por props
  const {
    nodeAnexosById,
    addNodeAnexoFiles,
    removeNodeAnexoFile,
    downloadNodeAnexoFile,
    semearComoCarregado,
    limparAnexosNodesRemovidos,
    removerAnexosDosNodes,
    nodeTemAnexosSalvos,
  } = nodeAnexos || {};

  // Hook para controle de modais e aplicação de templates
  const {
    templatesModalOpen,
    setTemplatesModalOpen,
    salvarTemplateModalOpen,
    setSalvarTemplateModalOpen,
    templateAplicarConfirm,
    setTemplateAplicarConfirm,
    aplicarTemplate,
    executarAplicacaoTemplate,
  } = useAplicarTemplate({
    nodes,
    setNodes,
    setEdges,
    setSelectedNodeId,
    setSelectedEdgeId,
    historyRef,
    redoRef,
    limparAnexosNodesRemovidos,
    semearComoCarregado,
    theme,
    setSnackbar,
  });

  // Carrega tipos personalizados persistidos na montagem inicial
  useEffect(() => {
    ensureCustomEquipmentTypesLoaded?.();
  }, []);

  // Mescla tipos padrão do sistema com tipos personalizados da store
  const equipmentTypesMesclados = useMemo(
    () => mergeEquipmentTypes(customEquipmentTypes),
    [customEquipmentTypes],
  );

  // Oculta destaque visual de seleção enquanto o modal de exclusão estiver aberto
  const suprimirDestaqueSelecao = Boolean(deleteNodeConfirm);

  // Identificadores de hosts vinculados a nós do diagrama
  const linkedHostIds = useMemo(
    () => [
      ...new Set(
        (nodes || []).map((node) => node?.data?.hostId).filter(Boolean),
      ),
    ],
    [nodes],
  );

  // Mapa de status SNMP e PING dos hosts vinculados
  const hostStatusMap = useHostStatus(linkedHostIds, isActive);

  // Mapa de rótulos legíveis de hosts por hostId para uso em tooltips
  const hostLabelByHostId = useMemo(
    () =>
      Object.fromEntries(
        (hostOptions || []).map((opcao) => [opcao?.hostId, opcao?.host]),
      ),
    [hostOptions],
  );

  // Injeta catálogo, paleta de cores e status de hosts nos nós para renderização
  const nodesForCanvas = useMemo(
    () =>
      (nodes || []).map((node) => ({
        ...node,
        selected: suprimirDestaqueSelecao ? false : node?.selected,
        data: {
          ...node?.data,
          _equipmentTypes: equipmentTypesMesclados,
          _colorSwatch: getAccessibleNodeColor(node?.data?.cor, theme),
          _hostStatus: node?.data?.hostId
            ? hostStatusMap?.[node?.data?.hostId]
            : undefined,
          _hostLabel: node?.data?.hostId
            ? hostLabelByHostId?.[node?.data?.hostId]
            : undefined,
        },
      })),
    [
      nodes,
      equipmentTypesMesclados,
      suprimirDestaqueSelecao,
      theme,
      hostStatusMap,
      hostLabelByHostId,
    ],
  );

  // Alterna exibição entre canvas embutido e modo tela cheia
  const toggleExpanded = () => onToggleExpanded?.();

  // Fecha tela cheia com tecla Escape e bloqueia rolagem do body de fundo
  useEffect(() => {
    if (!isExpanded) return undefined;

    const previousOverflow = document?.body?.style?.overflow ?? "";
    const handleKeyDown = (evento) => {
      if (evento?.key === "Escape") onToggleExpanded?.();
    };

    document?.addEventListener?.("keydown", handleKeyDown);
    if (document?.body?.style) document.body.style.overflow = "hidden";

    return () => {
      document?.removeEventListener?.("keydown", handleKeyDown);
      if (document?.body?.style)
        document.body.style.overflow = previousOverflow;
    };
  }, [isExpanded, onToggleExpanded]);

  // Centraliza o diagrama assim que o overlay recebe as dimensões da tela
  useEffect(() => {
    if (!isExpanded) return undefined;

    const frame = window?.requestAnimationFrame?.(() =>
      fitView({ padding: 0.2 }),
    );

    return () => window?.cancelAnimationFrame?.(frame);
  }, [fitView, isExpanded]);

  // Adiciona um novo equipamento na área central visível do canvas
  const addEquipamento = (tipo) => {
    if (!editMode || !tipo) return;
    pushHistory?.();
    const rect = document
      ?.querySelector(`[data-diagrama="${diagramInstanceKey}"]`)
      ?.getBoundingClientRect();
    const cascata = (nodes?.length || 0) % 4;
    const screenPos = rect
      ? {
          x: (rect?.left || 0) + (rect?.width || 0) / 2 - 60 + cascata * 32,
          y: (rect?.top || 0) + (rect?.height || 0) / 2 - 30 + cascata * 32,
        }
      : { x: 200, y: 150 };
    const position = screenToFlowPosition(screenPos);
    const novoNode = {
      id: `equip-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
      type: "equipamento",
      position,
      data: {
        tipo: tipo?.id,
        nome: "",
        observacoes: "",
      },
    };
    setNodes?.((prev) => [...(prev || []), novoNode]);
  };

  // Atualiza campo de dados do nó selecionado e grava histórico
  const updateNodeData = (campo, valor) => {
    if (nodeEditSnapshotRef?.current !== selectedNodeId) {
      pushHistory?.();
      if (nodeEditSnapshotRef) nodeEditSnapshotRef.current = selectedNodeId;
    }
    setNodes?.((prev) =>
      (prev || []).map((node) =>
        node?.id === selectedNodeId
          ? { ...node, data: { ...node?.data, [campo]: valor } }
          : node,
      ),
    );
  };

  // Atualiza label, estilo (traço) ou modo de linha da conexão selecionada. `style` não é mais
  // setado aqui: edgesForCanvas recalcula sempre a partir de data.lineStyle + tema atual (nunca
  // persistido — ver comentário em getEdgeVisualStyle/getDiagramData)
  const updateEdgeData = (campo, valor) => {
    if (edgeEditSnapshotRef?.current !== selectedEdgeId) {
      pushHistory?.();
      if (edgeEditSnapshotRef) edgeEditSnapshotRef.current = selectedEdgeId;
    }
    setEdges?.((prev) =>
      (prev || []).map((edge) => {
        if (edge?.id !== selectedEdgeId) return edge;
        if (campo === "lineStyle") {
          return { ...edge, data: { ...edge?.data, lineStyle: valor } };
        }
        if (campo === "lineMode") {
          return { ...edge, data: { ...edge?.data, lineMode: valor } };
        }
        if (campo === "label") {
          return { ...edge, label: valor };
        }
        return edge;
      }),
    );
  };

  // Executa exclusão de nós e conexões removendo anexos vinculados em cascata
  const executarDelete = useCallback(
    (nodeIds, edgeIds = []) => {
      const idsNodesValidos = [...new Set((nodeIds || []).filter(Boolean))];
      const idsEdgesValidas = new Set((edgeIds || []).filter(Boolean));
      if (!idsNodesValidos?.length && !idsEdgesValidas?.size) return;
      pushHistory?.();
      setDeleteNodeConfirm(null);

      const idsNodesSet = new Set(idsNodesValidos);
      removerAnexosDosNodes?.(idsNodesValidos);

      setNodes?.((prev) =>
        (prev || []).filter((node) => !idsNodesSet.has(node?.id)),
      );
      setEdges?.((prev) =>
        (prev || []).filter(
          (edge) =>
            !idsNodesSet.has(edge?.source) &&
            !idsNodesSet.has(edge?.target) &&
            !idsEdgesValidas.has(edge?.id),
        ),
      );
      setSelectedNodeId?.((atual) => (idsNodesSet.has(atual) ? null : atual));
      setSelectedEdgeId?.((atual) => (idsEdgesValidas.has(atual) ? null : atual));
    },
    [
      pushHistory,
      removerAnexosDosNodes,
      setNodes,
      setEdges,
      setSelectedNodeId,
      setSelectedEdgeId,
    ],
  );

  // Solicita exclusão com confirmação se houver nós com anexos salvos
  const requestDelete = useCallback(
    (nodeIds, edgeIds = []) => {
      const idsNodesValidos = [...new Set((nodeIds || []).filter(Boolean))];
      const idsEdgesValidas = [...new Set((edgeIds || []).filter(Boolean))];
      if (!idsNodesValidos?.length && !idsEdgesValidas?.length) return;
      const exigeConfirmacao = idsNodesValidos.some((id) =>
        nodeTemAnexosSalvos?.(id),
      );
      if (exigeConfirmacao) {
        setDeleteNodeConfirm({
          nodeIds: idsNodesValidos,
          edgeIds: idsEdgesValidas,
        });
        return;
      }
      executarDelete(idsNodesValidos, idsEdgesValidas);
    },
    [nodeTemAnexosSalvos, executarDelete],
  );

  // Exclui o nó selecionado no painel de detalhes
  const deleteSelectedNode = useCallback(() => {
    if (!selectedNodeId) return;
    requestDelete([selectedNodeId]);
  }, [selectedNodeId, requestDelete]);

  // Exclui a conexão selecionada no painel de detalhes
  const deleteSelectedEdge = useCallback(() => {
    if (!selectedEdgeId) return;
    requestDelete([], [selectedEdgeId]);
  }, [selectedEdgeId, requestDelete]);

  // Exclui todos os elementos atualmente selecionados no canvas
  const deleteSelection = useCallback(() => {
    const nodeIds = (nodes || [])
      .filter((node) => node?.selected)
      .map((node) => node?.id);
    const edgeIds = (edges || [])
      .filter((edge) => edge?.selected)
      .map((edge) => edge?.id);
    requestDelete(nodeIds, edgeIds);
  }, [nodes, edges, requestDelete]);

  // Insere cópias de nós e arestas deslocadas no canvas com novos IDs
  const inserirClone = useCallback(
    (nodesOrigem, edgesOrigem) => {
      if (!nodesOrigem?.length) return;
      pushHistory?.();

      const OFFSET = 40;
      const idMap = {};
      const novosNodes = nodesOrigem.map((node) => {
        const novoId = `equip-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
        idMap[node?.id] = novoId;
        return {
          ...node,
          id: novoId,
          position: {
            x: (node?.position?.x || 0) + OFFSET,
            y: (node?.position?.y || 0) + OFFSET,
          },
          data: { ...node?.data },
          selected: true,
        };
      });

      const novasEdges = (edgesOrigem || [])
        .map((edge) => {
          const novoSource = idMap[edge?.source];
          const novoTarget = idMap[edge?.target];
          if (!novoSource || !novoTarget) return null;
          return {
            ...edge,
            id: `equip-edge-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
            source: novoSource,
            target: novoTarget,
            data: { ...edge?.data },
            selected: true,
          };
        })
        .filter(Boolean);

      setNodes?.((prev) =>
        (prev || [])
          .map((node) => (node?.selected ? { ...node, selected: false } : node))
          .concat(novosNodes),
      );
      setEdges?.((prev) =>
        (prev || [])
          .map((edge) => (edge?.selected ? { ...edge, selected: false } : edge))
          .concat(novasEdges),
      );
      semearComoCarregado?.(novosNodes.map((node) => node?.id));
      setSelectedNodeId?.(novosNodes.length === 1 ? novosNodes[0].id : null);
      setSelectedEdgeId?.(null);
    },
    [
      pushHistory,
      setNodes,
      setEdges,
      semearComoCarregado,
      setSelectedNodeId,
      setSelectedEdgeId,
    ],
  );

  // Cola nós e conexões do clipboard global no canvas
  const pasteClipboard = useCallback(() => {
    if (!editMode) return;
    const clipboard = getDiagramClipboard();
    if (!clipboard?.nodes?.length) return;
    inserirClone(clipboard.nodes, clipboard.edges);
  }, [editMode, inserirClone]);

  // Duplica os elementos selecionados diretamente no canvas sem tocar no clipboard
  const duplicateSelection = useCallback(() => {
    if (!editMode) return;
    const nodesSelecionados = (nodes || []).filter((node) => node?.selected);
    if (!nodesSelecionados.length) return;
    const idsSelecionados = new Set(nodesSelecionados.map((node) => node?.id));
    const edgesInternas = (edges || []).filter(
      (edge) =>
        idsSelecionados.has(edge?.source) && idsSelecionados.has(edge?.target),
    );
    inserirClone(nodesSelecionados, edgesInternas);
  }, [editMode, nodes, edges, inserirClone]);

  // Aplica cor aos nós e/ou conexões atualmente selecionados (o menu de contexto só marca um tipo
  // por vez — ver handleNodeContextMenu/handleEdgeContextMenu — mas uma função só cobre os dois)
  const applyColorToSelection = useCallback(
    (cor) => {
      pushHistory?.();
      setNodes?.((prev) =>
        (prev || []).map((node) =>
          node?.selected
            ? { ...node, data: { ...node?.data, cor: cor || null } }
            : node,
        ),
      );
      setEdges?.((prev) =>
        (prev || []).map((edge) =>
          edge?.selected
            ? { ...edge, data: { ...edge?.data, cor: cor || null } }
            : edge,
        ),
      );
    },
    [pushHistory, setNodes, setEdges],
  );

  // Abre menu de contexto com opções para o nó clicado
  const handleNodeContextMenu = useCallback(
    (event, node) => {
      if (!editMode) return;
      event?.preventDefault?.();
      if (!node?.selected) {
        setNodes?.((prev) =>
          (prev || []).map((n) => ({ ...n, selected: n?.id === node?.id })),
        );
        setEdges?.((prev) =>
          (prev || []).map((e) => ({ ...e, selected: false })),
        );
        setSelectedNodeId?.(node?.id || null);
        setSelectedEdgeId?.(null);
      }
      setCanvasContextMenu({
        mouseX: event?.clientX,
        mouseY: event?.clientY,
        tipo: "node",
        hasClipboard: Boolean(getDiagramClipboard()?.nodes?.length),
      });
    },
    [editMode, setNodes, setEdges, setSelectedNodeId, setSelectedEdgeId],
  );

  // Abre menu de contexto com opções para a aresta clicada
  const handleEdgeContextMenu = useCallback(
    (event, edge) => {
      if (!editMode) return;
      event?.preventDefault?.();
      if (!edge?.selected) {
        setEdges?.((prev) =>
          (prev || []).map((e) => ({ ...e, selected: e?.id === edge?.id })),
        );
        setNodes?.((prev) =>
          (prev || []).map((n) => ({ ...n, selected: false })),
        );
        setSelectedEdgeId?.(edge?.id || null);
        setSelectedNodeId?.(null);
      }
      setCanvasContextMenu({
        mouseX: event?.clientX,
        mouseY: event?.clientY,
        tipo: "edge",
        hasClipboard: Boolean(getDiagramClipboard()?.nodes?.length),
      });
    },
    [editMode, setNodes, setEdges, setSelectedNodeId, setSelectedEdgeId],
  );

  // Abre menu de contexto na área vazia do canvas apenas se houver nós no clipboard
  const handlePaneContextMenu = useCallback(
    (event) => {
      if (!editMode) return;
      const hasClipboard = Boolean(getDiagramClipboard()?.nodes?.length);
      if (!hasClipboard) return;
      event?.preventDefault?.();
      setCanvasContextMenu({
        mouseX: event?.clientX,
        mouseY: event?.clientY,
        tipo: "pane",
        hasClipboard: true,
      });
    },
    [editMode],
  );

  // Abre menu de contexto ao clicar com botão direito sobre seleção múltipla
  const handleSelectionContextMenu = useCallback(
    (event) => {
      if (!editMode) return;
      event?.preventDefault?.();
      setCanvasContextMenu({
        mouseX: event?.clientX,
        mouseY: event?.clientY,
        tipo: "node",
        hasClipboard: Boolean(getDiagramClipboard()?.nodes?.length),
      });
    },
    [editMode],
  );

  // Registra atalhos de teclado e gerenciador de cola integrado
  useDiagramaAtalhos({
    editMode,
    isActive,
    selectedNodeId,
    addNodeAnexoFiles,
    handleUndo,
    handleRedo,
    copySelection,
    pasteClipboard,
    duplicateSelection,
    selectAll,
    deleteSelection,
    // Mesmo handleSave/loading do botão Salvar (cabeçalho e barra do diagrama ampliado) — sem
    // lógica própria, só o atalho a mais
    handleSalvar: () => {
      if (!salvandoCircuito) onSalvarCircuito?.();
    },
  });

  // Atualiza pontos de curvatura customizados da aresta selecionada
  const updateEdgeControlPoints = useCallback(
    (edgeId, points) => {
      if (edgeEditSnapshotRef?.current !== edgeId) {
        pushHistory?.();
        if (edgeEditSnapshotRef) edgeEditSnapshotRef.current = edgeId;
      }
      setEdges?.((prev) =>
        (prev || []).map((edge) =>
          edge?.id === edgeId
            ? { ...edge, data: { ...edge?.data, controlPoints: points } }
            : edge,
        ),
      );
    },
    [setEdges, pushHistory, edgeEditSnapshotRef],
  );

  // Nó e conexão atualmente selecionados para edição no painel lateral
  const selectedNode =
    nodes?.find((node) => node?.id === selectedNodeId) || null;
  const selectedEdge =
    edges?.find((edge) => edge?.id === selectedEdgeId) || null;

  // Injeta callback de atualização de curvas e recalcula o estilo visual (cor/traço) da conexão a
  // partir de data.lineStyle + tema atual — nunca confia num `style` que porventura já esteja no
  // estado (nem no que vier de um diagrama salvo antes dessa mudança de campo, ver
  // getDiagramData/resolveLineStyle), mesmo raciocínio dos dois previews read-only
  const edgesForCanvas = useMemo(
    () =>
      (edges || []).map((edge) => ({
        ...edge,
        selected: suprimirDestaqueSelecao ? false : edge?.selected,
        style: getEdgeVisualStyle(resolveLineStyle(edge?.data), theme, edge?.data?.cor),
        data: {
          ...edge?.data,
          ...(editMode
            ? { onControlPointsChange: updateEdgeControlPoints }
            : {}),
        },
      })),
    [edges, editMode, updateEdgeControlPoints, suprimirDestaqueSelecao, theme],
  );

  // Nós pendentes de exclusão com contagem de equipamentos que possuem anexos
  const nodesPendentesConfirmacao = deleteNodeConfirm?.nodeIds
    ? (nodes || []).filter((node) =>
        deleteNodeConfirm?.nodeIds?.includes(node?.id),
      )
    : [];
  const nodesPendentesComAnexo = nodesPendentesConfirmacao.filter((node) =>
    nodeTemAnexosSalvos?.(node?.id),
  );
  const deleteConfirmHeaderText =
    nodesPendentesConfirmacao?.length === 1
      ? `Excluir "${nodesPendentesConfirmacao[0]?.data?.nome || "equipamento"}"?`
      : `Excluir ${nodesPendentesConfirmacao?.length || 0} equipamentos selecionados?`;
  const deleteConfirmContentText =
    nodesPendentesConfirmacao?.length === 1
      ? "Este equipamento possui arquivos vinculados. Ao excluí-lo, os arquivos serão removidos ao salvar o circuito."
      : `${nodesPendentesComAnexo?.length || 0} de ${nodesPendentesConfirmacao?.length || 0} possuem arquivos vinculados. Ao excluir, os arquivos serão removidos ao salvar o circuito.`;

  return (
    <Box
      sx={{
        ...canvasStyles.canvasShell,
        ...(isExpanded ? canvasStyles.canvasShellExpanded : {}),
      }}
      role={isExpanded ? "dialog" : undefined}
      aria-modal={isExpanded ? true : undefined}
      aria-label={isExpanded ? "Diagrama de equipamentos ampliado" : undefined}
    >
      {isExpanded && (
        <Box sx={canvasStyles.canvasActionBar}>
          <img src={logo} alt="Logo" style={canvasStyles.canvasActionLogo} />
          <Box sx={canvasStyles.canvasActionGroup}>
            {editMode && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<SaveOutlined fontSize="small" />}
                disabled={salvandoCircuito}
                onClick={() => onSalvarCircuito?.()}
              >
                Salvar
              </Button>
            )}
            <Tooltip title="Reduzir diagrama" arrow>
              <IconButton
                size="small"
                sx={canvasStyles.canvasActionButton}
                onClick={toggleExpanded}
                aria-label="Reduzir diagrama"
              >
                <FullscreenExitOutlined />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}
      <Box
        sx={{
          ...canvasStyles.canvasWrapper,
          ...(isExpanded ? canvasStyles.canvasWrapperExpanded : {}),
        }}
        data-diagrama={diagramInstanceKey}
      >
        <ReactFlow
          proOptions={{ hideAttribution: true }}
          nodes={nodesForCanvas}
          edges={edgesForCanvas}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesChange={handleNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={(evento, node) => {
            setSelectedNodeId?.(node?.id || null);
            setSelectedEdgeId?.(null);
          }}
          onEdgeClick={(evento, edge) => {
            setSelectedEdgeId?.(edge?.id || null);
            setSelectedNodeId?.(null);
          }}
          onPaneClick={() => {
            setSelectedNodeId?.(null);
            setSelectedEdgeId?.(null);
          }}
          onNodeContextMenu={handleNodeContextMenu}
          onEdgeContextMenu={handleEdgeContextMenu}
          onPaneContextMenu={handlePaneContextMenu}
          onSelectionContextMenu={handleSelectionContextMenu}
          nodesDraggable={editMode}
          nodesConnectable={editMode}
          connectionMode={ConnectionMode.Loose}
          elementsSelectable
          deleteKeyCode={null}
          snapToGrid={editMode}
          snapGrid={[10, 10]}
          colorMode={theme?.palette?.mode}
          defaultEdgeOptions={{
            type: "equipamento",
            style: getEdgeVisualStyle("solida", theme),
            labelStyle: {
              fill: theme?.palette?.text?.primary,
              fontSize: 11,
              fontWeight: 600,
            },
            labelBgStyle: {
              fill: theme?.palette?.background?.paper,
              fillOpacity: 0.9,
            },
            labelBgPadding: [6, 3],
            labelBgBorderRadius: 4,
          }}
          connectionLineStyle={{
            stroke: theme?.palette?.primary?.main,
            strokeWidth: 2,
          }}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.2}
        >
          <Background
            variant={BackgroundVariant.Dots}
            gap={20}
            size={1}
            color={getDiagramDotColor(theme)}
          />
          {/* Controles flutuantes de zoom, minimapa e templates */}
          <ControlesDiagrama
            editMode={editMode}
            isExpanded={isExpanded}
            onToggleExpanded={onToggleExpanded}
            onAbrirTemplates={() => setTemplatesModalOpen(true)}
            onSalvarTemplate={() => setSalvarTemplateModalOpen(true)}
            hasNodes={Boolean((nodes?.length || 0) > 0)}
          />

          {/* Paleta de tipos de equipamento com busca e suporte a tipos personalizados */}
          <PaletaEquipamentos
            equipmentTypes={equipmentTypesMesclados}
            editMode={editMode}
            onAdicionarNode={addEquipamento}
            setSnackbar={setSnackbar}
          />

          {/* Painel de edição do elemento selecionado — sem margin-top extra: o Panel já
          posiciona relativo ao canvas (que já reserva o espaço da barra de ações ampliada
          no layout flex do shell), igual à paleta de equipamentos (top-left, mesma referência) */}
          {(selectedNode || selectedEdge) && (
            <Panel position="top-right">
              {selectedNode ? (
                <NodeInspector
                  selectedNode={selectedNode}
                  editMode={editMode}
                  isExpanded={isExpanded}
                  updateNodeData={updateNodeData}
                  deleteSelectedNode={deleteSelectedNode}
                  equipmentTypes={equipmentTypesMesclados}
                  nodeAnexos={
                    nodeAnexosById?.[selectedNodeId] || emptyNodeAnexosEntry
                  }
                  onAddNodeAnexoFiles={(arquivos) =>
                    addNodeAnexoFiles(selectedNodeId, arquivos)
                  }
                  onRemoveNodeAnexoFile={(arquivo) =>
                    removeNodeAnexoFile(selectedNodeId, arquivo)
                  }
                  onDownloadNodeAnexoFile={downloadNodeAnexoFile}
                  hostOptions={hostOptions}
                  hostStatusMap={hostStatusMap}
                />
              ) : (
                <Box sx={canvasStyles.inspectorBox}>
                  <Typography sx={canvasStyles.inspectorTitle}>
                    Conexão
                  </Typography>
                  <TextField
                    select
                    label="Tipo de linha"
                    size="small"
                    fullWidth
                    autoComplete="off"
                    value={selectedEdge?.data?.lineMode || "curva"}
                    onChange={(evento) =>
                      updateEdgeData("lineMode", evento?.target?.value)
                    }
                    disabled={!editMode}
                  >
                    <MenuItem value="curva">Curva</MenuItem>
                    <MenuItem value="reta">Reta</MenuItem>
                    <MenuItem value="ortogonal">Ortogonal</MenuItem>
                    <MenuItem value="ortogonal-suave">Ortogonal suave</MenuItem>
                  </TextField>
                  <TextField
                    label="Label (ex: 1 Gbps)"
                    size="small"
                    fullWidth
                    autoComplete="off"
                    value={selectedEdge?.label || ""}
                    onChange={(evento) =>
                      updateEdgeData("label", evento?.target?.value)
                    }
                    disabled={!editMode}
                  />
                  <TextField
                    select
                    label="Estilo da linha"
                    size="small"
                    fullWidth
                    autoComplete="off"
                    value={resolveLineStyle(selectedEdge?.data)}
                    onChange={(evento) =>
                      updateEdgeData("lineStyle", evento?.target?.value)
                    }
                    disabled={!editMode}
                  >
                    <MenuItem value="solida">Contínua</MenuItem>
                    <MenuItem value="tracejada">Tracejada</MenuItem>
                    <MenuItem value="pontilhada">Pontilhada</MenuItem>
                  </TextField>
                  <Box sx={canvasStyles.inspectorDeleteBox}>
                    <Button
                      color="error"
                      size="small"
                      fullWidth
                      startIcon={<DeleteOutlineOutlined fontSize="small" />}
                      onClick={deleteSelectedEdge}
                      disabled={!editMode}
                      sx={canvasStyles.deleteButton}
                    >
                      Excluir conexão
                    </Button>
                  </Box>
                </Box>
              )}
            </Panel>
          )}
        </ReactFlow>
      </Box>

      {/* Menu de contexto (botão direito) de nó/edge/área vazia do canvas */}
      <Menu
        open={Boolean(canvasContextMenu)}
        onClose={() => setCanvasContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          canvasContextMenu
            ? { top: canvasContextMenu.mouseY, left: canvasContextMenu.mouseX }
            : undefined
        }
      >
        {/* Copiar/Duplicar só fazem sentido pra nó (e o clipboard/duplicação são node-based — ver
        copySelection/duplicateSelection em useDiagramaState/DiagramaCanvas): numa edge sozinha
        selecionada eram um no-op silencioso, então nem aparecem nesse menu */}
        {canvasContextMenu?.tipo === "node" && (
          <MenuItem
            onClick={() => {
              copySelection();
              setCanvasContextMenu(null);
            }}
          >
            <ContentCopyOutlined fontSize="small" sx={{ mr: 1 }} /> Copiar
          </MenuItem>
        )}
        {canvasContextMenu?.hasClipboard && (
          <MenuItem
            onClick={() => {
              pasteClipboard();
              setCanvasContextMenu(null);
            }}
          >
            <ContentPasteOutlined fontSize="small" sx={{ mr: 1 }} /> Colar
          </MenuItem>
        )}
        {canvasContextMenu?.tipo === "node" && (
          <MenuItem
            onClick={() => {
              duplicateSelection();
              setCanvasContextMenu(null);
            }}
          >
            <FileCopyOutlined fontSize="small" sx={{ mr: 1 }} /> Duplicar
          </MenuItem>
        )}
        {(canvasContextMenu?.tipo === "node" || canvasContextMenu?.tipo === "edge") && (
          <Box onClick={(evento) => evento?.stopPropagation?.()}>
            <Divider />
            <Typography sx={canvasStyles.contextMenuColorLabel}>Cor</Typography>
            <NodeColorSwatches
              onSelectColor={(cor) => {
                applyColorToSelection(cor);
                setCanvasContextMenu(null);
              }}
            />
          </Box>
        )}
        {canvasContextMenu?.tipo !== "pane" && (
          <MenuItem
            onClick={() => {
              deleteSelection();
              setCanvasContextMenu(null);
            }}
            sx={{ color: "error.main" }}
          >
            <DeleteOutlineOutlined fontSize="small" sx={{ mr: 1 }} /> Excluir
          </MenuItem>
        )}
      </Menu>

      {/* Modal para listar e aplicar templates */}
      <ModalTemplates
        open={templatesModalOpen}
        onClose={() => setTemplatesModalOpen(false)}
        editMode={editMode}
        modo="individual"
        equipmentTypes={equipmentTypesMesclados}
        onAplicar={aplicarTemplate}
        setSnackbar={setSnackbar}
      />

      {/* Modal para salvar diagrama atual como template */}
      <ModalSalvarTemplate
        open={salvarTemplateModalOpen}
        onClose={() => setSalvarTemplateModalOpen(false)}
        mode="create"
        diagramData={{ nodes, edges }}
        setSnackbar={setSnackbar}
        onSaved={() =>
          setSnackbar?.({ children: "Template salvo.", severity: "success" })
        }
      />

      {/* Modal de confirmação antes de substituir diagrama por template */}
      <InternoModalConfirmacaoSaida
        openConfirmacao={Boolean(templateAplicarConfirm)}
        setOpenConfirmacao={(isOpen) => {
          if (!isOpen) setTemplateAplicarConfirm(null);
        }}
        title="Aplicar template?"
        headerText={`Substituir o diagrama atual pelo template "${templateAplicarConfirm?.nome || ""}"?`}
        contentText="Todos os equipamentos e conexões atuais serão removidos. Anexos dos equipamentos atuais serão excluídos ao salvar o circuito."
        confirmText="Aplicar"
        execute={() => executarAplicacaoTemplate(templateAplicarConfirm)}
        cancel={() => setTemplateAplicarConfirm(null)}
        severity="warning"
      />

      {/* Modal de confirmação antes de excluir equipamento que possui anexos */}
      <InternoModalConfirmacaoSaida
        openConfirmacao={Boolean(deleteNodeConfirm)}
        setOpenConfirmacao={(open) => {
          if (!open) setDeleteNodeConfirm(null);
        }}
        title="Excluir equipamento?"
        headerText={deleteConfirmHeaderText}
        contentText={deleteConfirmContentText}
        confirmText="Excluir"
        execute={() =>
          executarDelete(deleteNodeConfirm?.nodeIds, deleteNodeConfirm?.edgeIds)
        }
        cancel={() => setDeleteNodeConfirm(null)}
        severity="warning"
      />
    </Box>
  );
}
